import { produce } from 'immer';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import Button from '../../../../../../../common/widgets/Button';
import Input from '../../../../../../../common/widgets/Input';
import { ErrorState } from '../../../../../../benefit/models/Errors';
import { resources } from '../../../../../resources/resources';
import { ProductPreview } from '../../../Common/ProductPreview';
import { ProductPreviewData } from '../../types';

export interface ValueVoucherOfferDetailsForm {
  customVoucherDiscount?: string;
  customVoucherCode?: string;
  customVoucherUrl?: string;
}

interface ValueVoucherOfferDetailsProps {
  previewData?: ProductPreviewData;
  offerDetailsForm: ValueVoucherOfferDetailsForm;
  isSaveButtonLoading?: boolean;
  errors: ErrorState;
  setErrors: (errors: ErrorState) => void;
  setOfferDetailsForm: (form: ValueVoucherOfferDetailsForm) => void;
  buttonFunctions?: {
    onBackClick: () => void;
    onContinueClick: () => void;
  };
}

export const ValueVoucherOfferDetails = (
  props: ValueVoucherOfferDetailsProps,
) => {
  const {
    previewData,
    isSaveButtonLoading,
    offerDetailsForm,
    setOfferDetailsForm,
    errors,
    setErrors,
    buttonFunctions,
  } = props;
  const { t: translate } = useTranslation();

  const clearErrors = (field: string) => {
    setErrors(
      produce(errors, d => {
        d[field] = [];
      }),
    );
  };

  return (
    <>
      {previewData && (
        <ProductPreview
          productTypes={previewData.productTypes}
          producyType={previewData.producyType}
          productStatuses={previewData.productStatuses}
          status={previewData.status}
          name={previewData.name}
          imageContent={previewData.imageContent}
          showPricePreview={false}
        />
      )}
      <div className="m-panel">
        <h1>{translate(resources.products.add.offerDetails.title)}</h1>
        <div className="m-form">
          <div className="m-form-section">
            <div className="m-form-label">
              <h4>
                {translate(
                  resources.products.add.offerDetails.valueVoucher
                    .customVoucherDiscount.label,
                )}
                {'*'}
              </h4>
            </div>
            <div className="m-form-input">
              <Input
                value={offerDetailsForm.customVoucherDiscount}
                onChange={e =>
                  setOfferDetailsForm(
                    produce(offerDetailsForm, d => {
                      d.customVoucherDiscount = e.value
                        ? _.parseInt(e.value).toString()
                        : '';
                    }),
                  )
                }
                type="number"
                mandatory
                errors={errors.customVoucherDiscount}
                clearErrors={() => clearErrors('customVoucherDiscount')}
              />
              <span className="m-form-info">
                {translate(
                  resources.products.add.offerDetails.valueVoucher
                    .customVoucherDiscount.info,
                )}
              </span>
            </div>
          </div>

          <div className="m-form-section">
            <div className="m-form-label">
              <h4>
                {translate(
                  resources.products.add.offerDetails.valueVoucher
                    .customVoucherCode.label,
                )}
                {'*'}
              </h4>
            </div>
            <div className="m-form-input">
              <Input
                value={offerDetailsForm.customVoucherCode}
                onChange={e =>
                  setOfferDetailsForm(
                    produce(offerDetailsForm, d => {
                      d.customVoucherCode = e.value;
                    }),
                  )
                }
                mandatory
                errors={errors.customVoucherCode}
                clearErrors={() => clearErrors('customVoucherCode')}
              />
              <span className="m-form-info">
                {translate(
                  resources.products.add.offerDetails.valueVoucher
                    .customVoucherCode.info,
                )}
              </span>
            </div>
          </div>

          <div className="m-form-section">
            <div className="m-form-label">
              <h4>
                {translate(
                  resources.products.add.offerDetails.valueVoucher
                    .customVoucherUrl.label,
                )}
                {'*'}
              </h4>
            </div>
            <div className="m-form-input">
              <Input
                value={offerDetailsForm.customVoucherUrl}
                onChange={e =>
                  setOfferDetailsForm(
                    produce(offerDetailsForm, d => {
                      d.customVoucherUrl = e.value;
                    }),
                  )
                }
                mandatory
                errors={errors.customVoucherUrl}
                clearErrors={() => clearErrors('customVoucherUrl')}
                placeholder={translate(
                  resources.products.add.offerDetails.valueVoucher
                    .customVoucherUrl.placeholder,
                )}
              />
              <span className="m-form-info">
                {translate(
                  resources.products.add.offerDetails.valueVoucher
                    .customVoucherUrl.info,
                )}
              </span>
            </div>
          </div>
        </div>
      </div>
      {buttonFunctions && (
        <div className="m-actions-panel">
          <Button
            onClick={buttonFunctions.onBackClick}
            className="btn btn-outline"
            disabled={isSaveButtonLoading}
          >
            <span className="btn-text">{translate(resources.back)}</span>
          </Button>
          <Button
            onClick={buttonFunctions.onContinueClick}
            className="btn btn-solid"
            loading={isSaveButtonLoading}
          >
            <span className="btn-text">
              {translate(resources.saveAndContinue)}
            </span>
          </Button>
        </div>
      )}
    </>
  );
};
